import CustomStore from 'devextreme/data/custom_store';
import { unpack, pack } from 'msgpackr';
import stringify from '../utils/stableStringify';
import AntAPI from '../utils/AntAPI';
import AntTime from '../utils/AntTime';
import AntArray from '../utils/AntArray';
import AntString from '../utils/AntString';
import AntLocalStorage from '../utils/AntLocalStorage';
import AntOnboardingCache from '../utils/AntOnboardingCache';
import {
    SALES_PRODUCT_CATALOG_CACHE_NAME,
    SALES_PRODUCT_CATALOG_FIELDS,
    getSalesProductCatalogCacheKey,
    getRowCatalogueValues
} from '../utils/AntSalesProductCatalog';

import stringSimilarity from 'string-similarity';
import format from 'string-format';
import KhongDau from 'khong-dau';

export default class AntQueryStore {
    constructor(args) {
        this.config = args;
        this.getSearchConditions = args.getSearchConditions;
        this.getParentSearchConditions = args.getParentSearchConditions;
        this.getFilteredGroupConditions = args.getFilteredGroupConditions;
        this.getOrderings = args.getOrderings;
        this.getQueryFields = args.getQueryFields;
        this.getSearchText = args.getSearchText;
        this.getPageSize = args.getPageSize;
        this.getPageIndex = args.getPageIndex;
        this.setPageIndex = args.setPageIndex;
        this.getOrderBy = args.getOrderBy;
        this.getOrderType = args.getOrderType;
        this.isAllowQuery = args.isAllowQuery;
        this.onGotData = args.onGotData;
        this.onLoaded = args.onLoaded;
        this.onLoading = args.onLoading;
        this.onFiltering = args.onFiltering;

        this.isScrolling = args.isScrolling !== undefined ? args.isScrolling : false;
        this.remotePaging = args.remotePaging !== undefined ? args.remotePaging : true;
        this.loadFunc = args.loadFunc ? args.loadFunc : 'getList';

        this.API = args.API;
        this.url = args.url;

        this.initConditions = this.initConditions.bind(this);
        this.callAPI = this.callAPI.bind(this);
        this.loadDataSource = this.loadDataSource.bind(this);

        this.refresh = this.refresh.bind(this);
        this.cleanCached = this.cleanCached.bind(this);
        this.reset = this.reset.bind(this);
        this.resetCurrentData = this.resetCurrentData.bind(this);
        this.getCurrentData = this.getCurrentData.bind(this);
        this.getCurrentItem = this.getCurrentItem.bind(this);
        this.getCachedData = this.getCachedData.bind(this);
        this.getCachedItem = this.getCachedItem.bind(this);

        this.getItem = this.getItem.bind(this);
        this.patchRows = this.patchRows.bind(this);

        this.willGetLastResult = this.willGetLastResult.bind(this);
        this.prepareCurrentDataReload = this.prepareCurrentDataReload.bind(this);

        this.isQuerried = this.isQuerried.bind(this);

        this.options = {
            key: args.key ? args.key : 'id',
            load: this.loadDataSource,
            byKey: this.getItem
        };
        this.dataSource = new CustomStore(this.options);

        this.currentData = {
            data: [],
            totalCount: 0,
            conditions: []
        };

        this.cachedItems = [];
        // Keep the complete client-side snapshot separately from the current
        // page. POS category bars must be built from this list, not from a
        // separate postSearchPrepare request or only the first rendered page.
        this.cachedSnapshotRows = [];
        this.lastResult = null;
        this.isGetLastResult = false;
        this.forceCacheRefresh = false;
        this.forceCacheRefreshOptions = null;
    }

    reInit(args) {
        this.config = args;
        this.getSearchConditions = args.getSearchConditions;
        this.getParentSearchConditions = args.getParentSearchConditions;
        this.getFilteredGroupConditions = args.getFilteredGroupConditions;
        this.getOrderings = args.getOrderings;
        this.getQueryFields = args.getQueryFields;
        this.getSearchText = args.getSearchText;
        this.getPageSize = args.getPageSize;
        this.getPageIndex = args.getPageIndex;
        this.setPageIndex = args.setPageIndex;
        this.getOrderBy = args.getOrderBy;
        this.getOrderType = args.getOrderType;
        this.isAllowQuery = args.isAllowQuery;
        this.onGotData = args.onGotData;
        this.onLoaded = args.onLoaded;
        this.onLoading = args.onLoading;

        this.isScrolling = args.isScrolling !== undefined ? args.isScrolling : false;
        this.remotePaging = args.remotePaging !== undefined ? args.remotePaging : true;
        this.loadFunc = args.loadFunc ? args.loadFunc : 'getList';

        this.API = args.API;
        this.url = args.url;

        this.options = {
            key: args.key ? args.key : 'id',
            load: this.loadDataSource,
            byKey: this.getItem
        };

        if (!this.dataSource) {
            this.dataSource = new CustomStore(this.options);
        } else {
            if (args.forceReload) this.dataSource.load(this.options);
        }
    }

    instance() {
        return this.dataSource;
    }

    getItem(keyValue) {
        var keyName = this.options.key;
        var item;
        if (!item && this.currentData.data instanceof Array) item = this.currentData.data.find((c) => c && c[keyName] === keyValue);
        if (!item && this.cachedItems instanceof Array) item = this.cachedItems.find((c) => c && c[keyName] === keyValue);

        return item;
    }

    getCurrentData() {
        return this.currentData.data;
    }

    getCachedSnapshotRows() {
        return this.cachedSnapshotRows instanceof Array ? this.cachedSnapshotRows : [];
    }

    getCurrentItem(keyValue) {
        var keyName = this.options.key;
        var ar = this.getCurrentData();
        if (ar instanceof Array) return ar.find((c) => c && c[keyName] === keyValue);

        return null;
    }

    getCachedData() {
        return this.cachedItems;
    }

    getCachedItem(keyValue) {
        var keyName = this.options.key;
        if (this.cachedItems instanceof Array) return this.cachedItems.find((c) => c && c[keyName] === keyValue);

        return null;
    }

    async refresh() {
        await this.dataSource.load();
    }

    async reset(cleanCached) {
        await this.resetCurrentData(null, cleanCached);
    }

    isSelectedAll() {
        return this.currentData && this.editedItems && this.currentData.totalCount > 0 && this.currentData.totalCount === this.editedItems.length;
    }

    cleanCached() {
        this.cachedItems = [];
    }

    async resetCurrentData(obj, cleanCached) {
        if (cleanCached) {
            this.cleanCached();
        }

        if (!obj || obj.data instanceof Array == false) {
            this.currentData = {
                data: [],
                totalCount: 0,
                conditions: []
            };
        } else {
            this.currentData = obj;

            if (this.currentData && this.currentData.data instanceof Array) {
                for (let i = 0; i < this.currentData.data.length; i++) {
                    const row = this.currentData.data[i];
                    if (!row.id) this.cachedItems.push(row);
                    else {
                        var found = this.cachedItems.find((c) => c.id === row.id);
                        if (!found) this.cachedItems.push(row);
                        else {
                            let keys = Object.keys(row);
                            for (let j = 0; j < keys.length; j++) {
                                const k = keys[j];
                                if (k !== 'id') found[k] = row[k];
                            }
                        }
                    }
                }
            }
        }

        if (obj && obj.data instanceof Array) {
            if (this.onLoaded) this.onLoaded(this.currentData);
        }
    }

    isEmptyColumn(colName) {
        var isEmpty = true;
        if (this.lastResult) {
            // if (this.currentData && this.currentData.totalCount > 0) {
            //     if (this.currentData.totalCount === this.currentData.data.length) {
            //         isEmpty =
            //             this.currentData.data.map((d) => d[colName]).filter((f) => f !== undefined && f !== null && f !== '' && f !== 0).length === 0;
            //     } else isEmpty = false;
            // }
            // if (colName === 'store_name' && this.isQuerried()) console.log('ant-log- this.currentData.data: ', this.currentData.data);
            if (this.isQuerried() && this.currentData && this.currentData.data.length > 0) {
                isEmpty =
                    this.currentData.data
                        .map((d) => d[colName])
                        .filter((f) => f !== undefined && f !== null && f !== '' && f !== 0 && stringify(f) !== '[]' && stringify(f) !== '')
                        .length === 0;
            } else isEmpty = false;
        } else isEmpty = false;

        return isEmpty;
    }

    isEmptyColumns(colNames) {
        var isEmpty = true;
        if (this.lastResult) {
            // if (this.currentData && this.currentData.totalCount > 0) {
            //     if (this.currentData.totalCount === this.currentData.data.length) {
            //         isEmpty =
            //             this.currentData.data.map((d) => d[colName]).filter((f) => f !== undefined && f !== null && f !== '' && f !== 0).length === 0;
            //     } else isEmpty = false;
            // }
            // if (colName === 'store_name' && this.isQuerried()) console.log('ant-log- this.currentData.data: ', this.currentData.data);
            if (this.isQuerried() && this.currentData && this.currentData.data.length > 0) {
                for (let i = 0; i < colNames.length; i++) {
                    const colName = colNames[i];
                    if (isEmpty)
                        isEmpty =
                            this.currentData.data
                                .map((d) => d[colName])
                                .filter(
                                    (f) =>
                                        f !== undefined && f !== null && f !== '' && f !== 0 && stringify(f) !== '[]' && stringify(f) !== ''
                                ).length === 0;
                }
            } else isEmpty = false;
        } else isEmpty = false;

        return isEmpty;
    }

    isSameValueColumn(colName) {
        if (!this.lastResult) return false;

        if (this.currentData && this.currentData.totalCount > 0 && this.currentData.totalCount === this.currentData.data.length) {
            return (
                this.currentData.data.map((d) => d[colName]).filter((f) => f !== undefined && f !== null && f !== '' && f !== 0).length ===
                this.currentData.totalCount
            );
        }
        return false;
    }

    async isQuerried() {
        return Boolean(this.lastResult);
    }

    async willGetLastResult() {
        this.isGetLastResult = true;
    }

    prepareCurrentDataReload() {
        const rows = this.currentData?.data instanceof Array ? this.currentData.data : [];
        this.lastResult = {
            conditions: this.lastResult?.conditions || this.currentData?.conditions || [],
            value: {
                ...(this.lastResult?.value || {}),
                rows,
                totalCount: rows.length
            }
        };
        this.isGetLastResult = true;
    }

    requestCacheRefresh(options = {}) {
        // The following datasource load must contact the backend and rebuild the
        // snapshot. This is intentionally one-shot so category/text changes keep
        // filtering the already prepared client-side catalogue.
        this.forceCacheRefresh = true;
        this.forceCacheRefreshOptions = options;
        this.isGetLastResult = false;
    }

    async loadDataSource(opts) {
        // if (this.remotePaging && opts && (opts.take === undefined || opts.skip === undefined)) return this.currentData;

        if (opts && ((opts.take === undefined && opts.skip !== undefined) || (opts.take !== undefined && opts.skip === undefined)))
            return this.currentData;

        var conditions = await this.initConditions(opts);

        // if (this.remotePaging === false && this.isScrolling) {
        //     if (this.lastResult) {
        //         var obj1 = {};
        //         conditions.forEach((c) => {
        //             obj1[c.name] = c.value;
        //         });

        //         var obj2 = {};
        //         this.lastResult.conditions.forEach((c) => {
        //             obj2[c.name] = c.value;
        //         });
        //         var isSame = stringify(obj1) === stringify(obj2);
        //         if (isSame) {
        //             if (this.onGotData) this.onGotData(this.currentData, this.cachedItems);
        //             return this.currentData;
        //         }
        //     }
        // }
        var isAllowQuery = true;

        if (this.isAllowQuery && this.isAllowQuery.constructor.name == 'Function') isAllowQuery = this.isAllowQuery();

        if (!isAllowQuery) {
            if (this.groupByField) {
                var ar = this.currentData && this.currentData.data instanceof Array ? this.currentData.data : [];

                var groups = AntArray.groupBy(
                    ar.map((d) => {
                        return {
                            ...d,
                            key: String(d[this.groupByField])
                        };
                    }),
                    'key'
                );

                var ar = Object.keys(groups).map((key) => {
                    return {
                        key: key,
                        items: groups[key]
                    };
                });

                if (this.onGotData)
                    this.onGotData(
                        {
                            data: ar,
                            totalCount: result.totalCount
                        },
                        this.cachedItems
                    );

                return {
                    data: ar,
                    totalCount: result.totalCount
                };
            }

            if (this.onGotData) this.onGotData(this.currentData, this.cachedItems);
            return this.currentData;
        }

        this.currentOpts = opts;
        var keyName = this.options.key;
        var result;

        await this.callAPI(opts);

        result = {
            data: [...this.currentData.data],
            totalCount: parseInt(this.currentData.totalCount)
        };

        if (this.cachedItems.length > 0)
            result.data = result.data.map((e) => {
                var obj = this.cachedItems.find((c) => c[keyName] === e[keyName]);
                return { ...obj, ...e };
            });

        if (result && result.data instanceof Array) {
            if (this.onFiltering && this.onFiltering.constructor.name == 'Function') result = await this.onFiltering(result);
        }

        this.isGetLastResult = false;

        if (result && result.data instanceof Array && result.totalCount === undefined) result.totalCount = result.data.length;

        if (this.onGotData) this.onGotData(result, this.cachedItems);

        return result;
    }

    async initConditions(opts) {
        if (this.getPageSize && this.getPageSize.constructor.name == 'Function') this.pageSize = this.getPageSize();
        else if (opts.take !== undefined && opts.take !== null) this.pageSize = opts.take;

        if (this.getSearchText && this.getSearchText.constructor.name == 'Function') this.searchText = this.getSearchText(opts);

        if (this.getPageIndex && this.getPageIndex.constructor.name == 'Function') this.pageIndex = this.getPageIndex(opts);
        else if (opts.take !== undefined && opts.take !== null && opts.skip !== undefined && opts.skip !== null)
            this.pageIndex = parseInt(opts.skip / opts.take);

        if (this.getOrderBy && this.getOrderBy.constructor.name == 'Function') this.orderBy = this.getOrderBy(opts);

        if (this.getOrderType && this.getOrderType.constructor.name == 'Function') this.orderType = this.getOrderType(opts);

        var condsObj = {};

        if (this.getSearchConditions && this.getSearchConditions.constructor.name == 'Function') {
            var obj = this.getSearchConditions();

            if (obj) {
                Object.keys(obj).forEach(function (key) {
                    if (obj[key] !== undefined) condsObj[key] = obj[key];
                    // conditions.push({
                    //     name: key,
                    //     value: obj[key]
                    // });
                });
            }
        }

        if (this.getParentSearchConditions && this.getParentSearchConditions.constructor.name == 'Function') {
            var obj = this.getParentSearchConditions();

            if (obj) {
                Object.keys(obj).forEach(function (key) {
                    if (obj[key] !== undefined) condsObj[key] = obj[key];
                    // conditions.push({
                    //     name: key,
                    //     value: obj[key]
                    // });
                });
            }
        }

        if (this.getFilteredGroupConditions && this.getFilteredGroupConditions.constructor.name == 'Function') {
            var obj = this.getFilteredGroupConditions();

            if (obj) {
                Object.keys(obj).forEach(function (key) {
                    if (obj[key] !== undefined) condsObj[key] = obj[key];
                    // conditions.push({
                    //     name: key,
                    //     value: obj[key]
                    // });
                });
            }
        }

        var conditions =
            Object.keys(condsObj).length > 0
                ? Object.keys(condsObj).map((key) => {
                      return {
                          name: key,
                          value: condsObj[key]
                      };
                  })
                : // [
                  //       {
                  //           name: 'conditions',
                  //           value: condsObj
                  //       }
                  //   ]
                  [];

        // if (opts.filter instanceof Array && opts.filter.length > 0) {
        //     if (opts.filter[0] instanceof Array) {
        //         opts.filter.map((f) => {
        //             if (f instanceof Array) {
        //                 conditions.push({
        //                     name: f[0],
        //                     value: f[2]
        //                 });
        //             }
        //         });
        //     } else
        //         conditions.push({
        //             name: opts.filter[0],
        //             value: opts.filter[2]
        //         });
        // }

        if (this.searchText !== undefined && this.searchText !== null && String(this.searchText).trim() !== '')
            conditions.push({
                name: 'searchText',
                value: this.searchText
            });

        if (this.getOrderings && this.getOrderings.constructor.name == 'Function') {
            var obj = this.getOrderings();

            if (obj instanceof Array && obj.length > 0)
                conditions.push({
                    name: 'ordering',
                    value: obj
                });
        }

        if (this.getQueryFields && this.getQueryFields.constructor.name == 'Function') {
            var obj = this.getQueryFields();

            if (obj instanceof Array && obj.length > 0) {
                conditions.push({
                    name: 'selectFields',
                    value: obj
                });
            }
        }
        if (this.orderBy !== undefined && this.orderBy !== null && String(this.orderBy).trim() !== '')
            conditions.push({
                name: 'orderBy',
                value: this.orderBy
            });

        if (this.orderType !== undefined && this.orderType !== null && String(this.orderType).trim() !== '')
            conditions.push({
                name: 'orderType',
                value: this.orderType
            });

        let pageSize = opts.take ? opts.take : this.pageSize;
        let pageIndex = opts.skip / pageSize;

        if (this.remotePaging && pageSize !== undefined && pageSize !== null)
            conditions.push({
                name: 'pageSize',
                value: pageSize
            });

        if (this.remotePaging && pageIndex !== undefined && pageIndex !== null)
            conditions.push({
                name: 'pageIndex',
                value: pageIndex
            });

        return conditions;
    }

    getCacheService() {
        return this.config.service || (this.API && this.API.entityName) || this.config.url;
    }

    isSalesProductCatalogCache() {
        return this.getCacheService() === 'ARItemStatus' && this.config.cacheData && this.config.cacheName === SALES_PRODUCT_CATALOG_CACHE_NAME;
    }

    getCacheGroup() {
        if (this.isSalesProductCatalogCache()) return SALES_PRODUCT_CATALOG_CACHE_NAME;
        let group = this.config.cacheName
            ? this.config.cacheName
            : this.config.url
            ? this.config.url
            : this.config.service
            ? this.config.service
            : '';
        return AntOnboardingCache.cacheGroup(group, this.getCacheService());
    }

    getCacheSeconds() {
        return Number(this.config.cacheSeconds) || 60 * 60;
    }

    getCacheSourceFields(field) {
        if (this.getCacheService() === 'ARItemStatus' && field === 'catalogue1') {
            return ['catalogue1', 'catalogue2', 'catalogue3'];
        }
        return [field];
    }

    getCacheFieldValues(row, field) {
        if (this.getCacheService() === 'ARItemStatus' && field === 'catalogue1') return getRowCatalogueValues(row);
        return this.getCacheSourceFields(field)
            .map((sourceField) => (row[sourceField] instanceof Array ? row[sourceField] : [row[sourceField]]))
            .flat();
    }

    clearExpiredCacheData() {
        let cacheGroup = this.getCacheGroup();
        AntLocalStorage.clearExpiredKeys(cacheGroup);
    }

    clearCacheData() {
        let cacheGroup = this.getCacheGroup();
        AntLocalStorage.clearByKey(cacheGroup);
    }

    normalizeCachedSelectFields(obj) {
        if (!(obj instanceof Array)) return obj;

        const selectFieldsCondition = obj.find((condition) => condition.name === 'selectFields');
        if (!selectFieldsCondition && this.isSalesProductCatalogCache()) {
            obj.push({ name: 'selectFields', value: SALES_PRODUCT_CATALOG_FIELDS });
            return obj;
        }
        if (!selectFieldsCondition || !(selectFieldsCondition.value instanceof Array)) return obj;

        const requestedFields = selectFieldsCondition.value
            .map((field) => (field && typeof field === 'object' ? field.field : field))
            .filter((field) => typeof field === 'string' && field.length > 0);
        const requiredFields = [
            ...(this.config.cacheByPassFields || []).map((field) => this.getCacheSourceFields(field)).flat(),
            ...(this.getCacheService() === 'ARItemStatus' ? SALES_PRODUCT_CATALOG_FIELDS : []),
            ...(this.config.searchTextFields || [])
        ].filter((field) => typeof field === 'string' && field.length > 0);

        // microbundle-crl transpiles `[...new Set(values)]` to
        // `[].concat(new Set(values))`, which becomes `[{}]` in the JSON body.
        // Array.from preserves the iterable conversion in the production build.
        selectFieldsCondition.value = Array.from(new Set([...requestedFields, ...requiredFields]));
        return obj;
    }

    async cacheData(objS, options = {}) {
        let cacheGroup = this.getCacheGroup();

        let obj = JSON.parse(stringify(objS));
        if (obj instanceof Array) {
            obj = obj
                .filter((o) => o.name !== 'pageIndex')
                .filter((o) => o.name !== 'pageSize')
                .filter((o) => o.name !== 'searchText')
                .filter((o) => o.name !== 'minimizeData')
                .filter((o) => o.name !== 'forceRecalc');
        }

        if (this.config.cacheByPassFields instanceof Array) {
            for (let i = 0; i < this.config.cacheByPassFields.length; i++) {
                const field = this.config.cacheByPassFields[i];

                // delete obj[field];
                if (obj instanceof Array) {
                    obj = obj.filter((o) => o.name !== field);
                }
            }
        }

        // A client-side snapshot must carry the values used for local category
        // and text filtering even when the card itself does not render them.
        // Normalize descriptors as a final guard before AntAPI serializes them.
        obj = this.normalizeCachedSelectFields(obj);

        const cacheKey = this.isSalesProductCatalogCache() ? getSalesProductCatalogCacheKey() : stringify(obj);
        const bypassConditions = options.byPassGetCache
            ? [
                  { name: 'byPassGetCache', value: true },
                  { name: 'forceQuery', value: true }
              ]
            : [];
        const query = [...obj, ...bypassConditions, { name: 'pageIndex', value: 0 }, { name: 'pageSize', value: 10000 }];

        try {
            const res = await this.API.getList(query, this.config.url);
            if (!(res?.rows instanceof Array)) return null;

            let rows = res.rows;
            const totalCount = Number(res.totalCount) || rows.length;
            let nextPageIndex = 1;
            while (rows.length < totalCount) {
                const nextResult = await this.API.getList(
                    [...obj, ...bypassConditions, { name: 'pageIndex', value: nextPageIndex }, { name: 'pageSize', value: 10000 }],
                    this.config.url,
                    { byPassTotal: true }
                );
                if (!(nextResult?.rows instanceof Array) || nextResult.rows.length === 0) break;
                rows = rows.concat(nextResult.rows);
                nextPageIndex += 1;
            }

            await AntLocalStorage.setData(cacheGroup, cacheKey, rows, this.getCacheSeconds());
            return rows;
        } catch (error) {
            // A transport failure is not a successful empty catalogue.
            return null;
        }
    }

    async getCacheData(objS, options = {}) {
        let cacheGroup = this.getCacheGroup();

        let obj = JSON.parse(stringify(objS));
        // delete obj.pageIndex;
        // delete obj.pageSize;
        // delete obj.searchText;
        // delete obj.minimizeData;
        // delete obj.forceRecalc;

        if (obj instanceof Array) {
            obj = obj
                .filter((o) => o.name !== 'pageIndex')
                .filter((o) => o.name !== 'pageSize')
                .filter((o) => o.name !== 'searchText')
                .filter((o) => o.name !== 'minimizeData')
                .filter((o) => o.name !== 'forceRecalc');
        }

        if (this.config.cacheByPassFields instanceof Array) {
            for (let i = 0; i < this.config.cacheByPassFields.length; i++) {
                const field = this.config.cacheByPassFields[i];
                // delete obj[field];
                if (obj instanceof Array) {
                    obj = obj.filter((o) => o.name !== field);
                }
            }
        }

        // Use the exact same canonical field list as cacheData(). Besides
        // rejecting `{}`, this keeps the read and write cache hashes identical.
        obj = this.normalizeCachedSelectFields(obj);

        let cacheKey = this.isSalesProductCatalogCache() ? getSalesProductCatalogCacheKey() : stringify(obj);
        const stored = await AntLocalStorage.getData(cacheGroup, cacheKey, { allowExpired: true, includeMeta: true });
        let rows = stored?.data;
        // A caller that has already attempted an explicit refresh must not make
        // a second identical request when the server is offline. It can still
        // use the existing snapshot as the safe fallback.
        if ((stored?.expired || !(rows instanceof Array)) && options.allowNetwork !== false) {
            const refreshedRows = await this.cacheData(objS);
            if (refreshedRows instanceof Array) rows = refreshedRows;
        }

        if (rows instanceof Array) {
            this.cachedSnapshotRows = rows;
            if (this.config.cacheByPassFields instanceof Array) {
                for (let i = 0; i < this.config.cacheByPassFields.length; i++) {
                    const field = this.config.cacheByPassFields[i];

                    let objFieldValue = objS.find((o) => o.name === field);
                    if (objFieldValue) objFieldValue = objFieldValue.value;

                    if (objFieldValue !== undefined && objFieldValue !== null && objFieldValue !== '') {
                        const selectedValues = (objFieldValue instanceof Array ? objFieldValue : [objFieldValue]).map((value) => String(value));
                        rows = rows.filter((row) => {
                            const rowValues = this.getCacheFieldValues(row, field);
                            return rowValues.some((value) => selectedValues.includes(String(value?.code !== undefined ? value.code : value)));
                        });
                    }
                }
            }

            let objSearchText = objS.find((o) => o.name === 'searchText');
            if (objSearchText) objSearchText = objSearchText.value;

            if (objSearchText && this.config.searchTextFields instanceof Array) {
                let strSearchText = KhongDau(AntString.compoundUnicode(objSearchText).toLowerCase()).trim();

                let contentRows = rows.map((row) => {
                    let content = this.config.searchTextFields
                        .map((f) => row[f] || '')
                        .join(' ')
                        .toLowerCase();

                    return {
                        rawRow: row,
                        content: KhongDau(AntString.compoundUnicode(content).toLowerCase()).trim(),
                        initials: KhongDau(AntString.compoundUnicode(content).toLowerCase())
                            .trim()
                            .split(/\s+/)
                            .filter(Boolean)
                            .map((word) => word.charAt(0))
                            .join('')
                    };
                });
                let rows2 = contentRows
                    .filter((row) => row.content.includes(strSearchText) || row.initials.includes(strSearchText))
                    .map((row) => row.rawRow);

                if (rows2.length === 0) {
                    rows2 = contentRows
                        .map((row) => ({ ...row, score: stringSimilarity.compareTwoStrings(row.content, strSearchText) }))
                        .filter((row) => row.score > 0.5)
                        .sort((left, right) => right.score - left.score)
                        .map((row) => row.rawRow);
                }
                rows = rows2;
            }
        }

        let objPageIndex = objS.find((o) => o.name === 'pageIndex');
        if (objPageIndex) objPageIndex = objPageIndex.value;

        let objPageSize = objS.find((o) => o.name === 'pageSize');
        if (objPageSize) objPageSize = objPageSize.value;

        let pageIndex = objPageIndex !== undefined ? objPageIndex : 0;
        let pageSize = objPageSize !== undefined ? objPageSize : 1000;

        const startIndex = pageIndex * pageSize;
        const endIndex = startIndex + pageSize;

        if (!(rows instanceof Array)) return null;
        rows = rows.slice(startIndex, endIndex);

        return rows;
    }

    async callAPI(opts) {
        // console.log('ant-log-callAPI-opts : ',opts);
        if (!this.API) return;

        // var cleanCached = true;
        // if (this.lastResult) {
        //     var lastConditions = this.lastResult.conditions;
        //     lastConditions = lastConditions.map((c) => {
        //         return {
        //             name: c.name,
        //             value: c.name === 'pageIndex' ? c.value : null
        //         };
        //     });
        //     var lastConditionsHash = crypto
        //         .createHash('md5')
        //         .update(stringify({ ...lastConditions }))
        //         .digest('hex');
        //     var currentConditions = conditions.map((c) => {
        //         return {
        //             name: c.name,
        //             value: c.name === 'pageIndex' ? c.value : null
        //         };
        //     });
        //     var currentConditionsHash = crypto
        //         .createHash('md5')
        //         .update(stringify({ ...currentConditions }))
        //         .digest('hex');
        //     cleanCached = lastConditionsHash !== currentConditionsHash;
        // }

        // var hashKey = crypto.createHash('md5').update(stringify(conditions)).digest('hex');

        var conditions = await this.initConditions(opts);
        // console.log('ant-log-callAPI-conditions : ',conditions);

        var results = null;

        if (this.onLoading) this.onLoading(conditions);

        var isCleanCached = false;
        if (this.lastResult) {
            var obj1 = {};
            conditions.forEach((c) => {
                if (!['pageIndex'].includes(c.name)) obj1[c.name] = c.value;
            });

            var obj2 = {};
            this.lastResult.conditions.forEach((c) => {
                if (!['pageIndex'].includes(c.name)) obj2[c.name] = c.value;
            });
            isCleanCached = stringify(obj1) !== stringify(obj2);
        }

        if (isCleanCached && this.pageIndex !== 0) {
            if (this.setPageIndex) {
                this.setPageIndex(0);
                return;
            }
        }

        const forceRefresh = this.forceCacheRefresh || opts?.forceQuery === true || opts?.forceRecalc === true;
        const forceRefreshOptions = this.forceCacheRefreshOptions || {};
        this.forceCacheRefresh = false;
        this.forceCacheRefreshOptions = null;

        // Chỉ dùng lại kết quả trước khi điều kiện tìm kiếm không đổi.
        const isSameAsLastResult = Boolean(this.lastResult) && stringify(conditions) === stringify(this.lastResult.conditions);
        if (this.isGetLastResult && isSameAsLastResult && !forceRefresh) {
            // && this.lastResult.hashKey && this.lastResult.hashKey === hashKey)
            results = this.lastResult.value;
        } else {
            let rows = [];
            let shouldSetData = false;
            // Cache is a fast/offline snapshot, not a substitute for an explicit
            // refresh. Rebuild the complete snapshot first, then apply category
            // and text filters locally from that same source. On a transport
            // failure retain the existing snapshot instead of clearing the grid.
            if (this.config.cacheData) {
                if (forceRefresh) {
                    await this.cacheData(conditions, forceRefreshOptions);
                    rows = await this.getCacheData(conditions, { allowNetwork: false });
                } else rows = await this.getCacheData(conditions);
                if (rows instanceof Array) {
                    results = { rows: rows };
                    shouldSetData = true;
                } else {
                    // Keep the visible grid intact when neither cache nor API can answer.
                    results = this.lastResult?.value || { rows: this.currentData?.data || [] };
                }
            } else {
                const requestConditions = forceRefreshOptions.byPassGetCache
                    ? [
                          ...conditions,
                          { name: 'byPassGetCache', value: true },
                          { name: 'forceQuery', value: true }
                      ]
                    : conditions;
                if (this.url) {
                    results = await this.API.getList(requestConditions, this.url, { byPassDefaultConds: true, byPassTotal: Boolean(this.isScrolling) });
                } else if (this.loadFunc) {
                    results = await AntAPI.prototype[this.loadFunc].call(this.API, requestConditions, null, { byPassTotal: Boolean(this.isScrolling) });
                } else {
                    results = await this.API.getList(requestConditions, null, { byPassTotal: Boolean(this.isScrolling) });
                }
            }
            this.lastResult = {
                // hashKey: hashKey,
                conditions: JSON.parse(stringify(conditions)),
                value: results
            };
        }

        var pageSize = null;
        var pageIndex = null;
        if (this.remotePaging && this.pageSize !== undefined && this.pageSize !== null) pageSize = this.pageSize;
        if (this.remotePaging && this.pageIndex !== undefined && this.pageIndex !== null) pageIndex = this.pageIndex;

        for (let i = 0; i < results.rows.length; i++) {
            const r = results.rows[i];
            var rIndex = pageSize !== null && pageIndex !== null ? pageSize * pageIndex + i : i;
            r.rowIndex = rIndex + 1;
        }

        var totalCount = results.totalCount !== undefined ? parseInt(results.totalCount) : results.rows.length;

        await this.resetCurrentData(
            {
                summary: results.summary,
                data: results.rows,
                totalCount: totalCount,
                conditions: conditions
            },
            isCleanCached
        );
    }

    async updateItem(keyValue, values, opts) {
        await this.updateDataSource(keyValue, values, opts);
    }

    patchRows(incomingRows, options = {}) {
        const rows = incomingRows instanceof Array ? incomingRows.filter(Boolean) : [];
        const matchFields = options.matchFields instanceof Array && options.matchFields.length > 0 ? options.matchFields : ['id', 'code'];
        const requestedValues = new Set((options.requestedValues || []).map((value) => String(value)));
        const incomingByKey = new Map();

        const getKey = (row) => {
            const field = matchFields.find((name) => row && row[name] !== undefined && row[name] !== null);
            return field ? `${field}:${String(row[field])}` : null;
        };

        rows.forEach((row) => {
            const key = getKey(row);
            if (key) incomingByKey.set(key, row);
        });

        const currentRows = options.replaceAll
            ? []
            : this.currentData && this.currentData.data instanceof Array
              ? this.currentData.data
              : [];
        const usedKeys = new Set();
        const nextRows = [];

        currentRows.forEach((row) => {
            const key = getKey(row);
            const incoming = key ? incomingByKey.get(key) : null;
            if (incoming) {
                usedKeys.add(key);
                const merged = { ...row, ...incoming, rowIndex: row.rowIndex };
                // Keep the original object when a lightweight polling response
                // contains no effective change. Card cells are memoized by row
                // reference, so this avoids repainting every table card on each
                // reconciliation tick.
                nextRows.push(stringify(row) === stringify(merged) ? row : merged);
                return;
            }

            const field = matchFields.find((name) => row && row[name] !== undefined && row[name] !== null);
            if (options.removeMissing && field && requestedValues.has(String(row[field]))) return;
            nextRows.push(row);
        });

        if (options.appendMissing !== false) {
            incomingByKey.forEach((row, key) => {
                if (!usedKeys.has(key)) nextRows.push(row);
            });
        }

        const orderFields = (options.orderBy instanceof Array ? options.orderBy : [options.orderBy]).filter(Boolean);
        if (orderFields.length > 0) {
            const direction = String(options.orderType || 'asc').toLowerCase() === 'desc' ? -1 : 1;
            nextRows.sort((left, right) => {
                for (let i = 0; i < orderFields.length; i++) {
                    const field = orderFields[i];
                    const leftValue = left && left[field];
                    const rightValue = right && right[field];
                    if (leftValue === rightValue) continue;
                    if (leftValue === undefined || leftValue === null) return direction;
                    if (rightValue === undefined || rightValue === null) return -direction;
                    const compared = String(leftValue).localeCompare(String(rightValue), undefined, {
                        numeric: true,
                        sensitivity: 'base'
                    });
                    if (compared !== 0) return compared * direction;
                }
                return 0;
            });
        }

        nextRows.forEach((row, index) => {
            if (row) row.rowIndex = index + 1;
        });

        this.currentData = {
            ...this.currentData,
            data: nextRows,
            totalCount: nextRows.length
        };
        this.cachedItems = nextRows;
        if (this.lastResult && this.lastResult.value) {
            this.lastResult.value = {
                ...this.lastResult.value,
                rows: nextRows,
                totalCount: nextRows.length
            };
        }
        return nextRows;
    }

    async updateDataSource(keyValue, values, opts) {
        console.log('ant-log-updateDataSource-keyValue : ', keyValue);
        console.log('ant-log-updateDataSource-values : ', values);
        var keyName = this.options.key;

        console.log('ant-log-updateDataSource-1 : ');
        var obj = {
            [keyName]: keyValue
        };

        if (this.currentData.data instanceof Array) {
            this.currentData.data.forEach((r) => {
                if (r && r[keyName] === keyValue) {
                    Object.assign(r, values);
                }
            });
        }

        if (this.cachedItems instanceof Array) {
            this.cachedItems.forEach((r) => {
                if (r && r[keyName] === keyValue) {
                    Object.assign(r, values);
                }
            });
        }

        console.log('ant-log-updateDataSource-this.currentData.data : ', this.currentData.data);
        console.log('ant-log-updateDataSource-this.cachedItems : ', this.cachedItems);
        if (this.editedItems instanceof Array) {
            this.editedItems.push({ ...obj, ...values });
        }

        if ((!opts || !opts.byPassEvent) && this.onUpdated) this.onUpdated(keyValue, values);
    }
}
